import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { formatAgo } from '../../chat.format';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatInvite } from '../../chat.model';

/**
 * Convites por aceitar (contrato 6.3, v8), no topo da lista de conversas: quem
 * convidou, o cliente, o imovel e uma linha da conversa, com Aceitar / Recusar.
 * So markup e @Outputs: quem responde (answer_invite) e o contentor. Mesmo desenho
 * da faixa "Assumir eu" (chat-assign-banner).
 */
@Component({
  selector: 'chat-invites',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<section class="chat-invites" [attr.aria-label]="strings.invitesTitle">
  <div class="chat-invites__h">
    <chat-icon name="userplus" [size]="14"></chat-icon>{{ strings.invitesTitle }}
    <span class="chat-invites__count">{{ invites.length }}</span>
  </div>
  <div *ngFor="let i of invites; trackBy: trackByChat" class="chat-invite">
    <chat-avatar [src]="photo(i.client?.profile)" [name]="i.client?.name || i.by?.name || '?'" [size]="36"></chat-avatar>
    <div class="chat-invite__body">
      <div class="chat-invite__text">{{ text(i) }}</div>
      <div class="chat-invite__meta">
        <span *ngIf="i.property?.reference" class="chat-invite__ref">{{ i.property?.reference }}</span>
        <span *ngIf="i.property?.title" class="chat-invite__title">{{ i.property?.title }}</span>
        <span class="chat-invite__ago">{{ ago(i.invitedAt) }}</span>
      </div>
      <div *ngIf="i.preview" class="chat-invite__preview">“{{ i.preview }}”</div>
      <div class="chat-invite__actions">
        <button type="button" class="chat-invite__btn chat-invite__btn--solid" [disabled]="busy === i.chat" (click)="accept.emit(i.chat)">
          <span *ngIf="busy === i.chat" class="chat-invite__spinner" aria-hidden="true"></span>{{ strings.inviteAccept }}
        </button>
        <button type="button" class="chat-invite__btn chat-invite__btn--soft" [disabled]="busy === i.chat" (click)="decline.emit(i.chat)">{{ strings.inviteDecline }}</button>
      </div>
    </div>
  </div>
</section>`,
  styles: [`
:host { display: block; flex-shrink: 0; max-height: 45%; overflow-y: auto; border-bottom: 1px solid var(--chat-line); }
.chat-invites { display: flex; flex-direction: column; gap: 8px; padding: 12px; background: rgba(var(--chat-accent-rgb), .05); }
.chat-invites__h { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; color: var(--chat-accent); }
.chat-invites__count { margin-left: auto; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px; background: var(--chat-accent); color: var(--chat-on-accent); font-size: 11px; line-height: 20px; text-align: center; }
.chat-invite { display: flex; gap: 10px; padding: 10px; border-radius: calc(var(--chat-radius) * 2); background: var(--chat-surface); box-shadow: var(--chat-shadow); animation: chat-invite-in .25s ease-out; }
.chat-invite__body { min-width: 0; flex-grow: 1; display: flex; flex-direction: column; gap: 3px; }
.chat-invite__text { font-size: 13px; font-weight: 500; color: var(--chat-ink); }
.chat-invite__meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 11.5px; color: var(--chat-muted); }
.chat-invite__ref { padding: 1px 6px; border-radius: 4px; background: rgba(var(--chat-accent-rgb), .1); color: var(--chat-accent); font-weight: 600; }
.chat-invite__title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 14em; }
.chat-invite__preview { font-size: 12px; color: var(--chat-muted); font-style: italic; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-invite__actions { display: flex; gap: 8px; margin-top: 6px; }
.chat-invite__btn { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px; border: 0; border-radius: var(--chat-radius); font: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; transition: background var(--chat-motion) ease, color var(--chat-motion) ease; }
.chat-invite__btn[disabled] { opacity: .65; cursor: default; }
.chat-invite__btn--solid { background: var(--chat-accent); color: var(--chat-on-accent); }
.chat-invite__btn--solid:hover:not([disabled]) { filter: brightness(1.08); }
.chat-invite__btn--soft { background: rgba(var(--chat-dark-rgb), .06); color: var(--chat-ink); }
.chat-invite__btn--soft:hover:not([disabled]) { background: rgba(var(--chat-danger-rgb), .12); color: var(--chat-danger); }
.chat-invite__btn:focus-visible { outline: 2px solid var(--chat-ink); outline-offset: 2px; }
.chat-invite__spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(255, 255, 255, .45); border-top-color: #ffffff; animation: chat-invite-spin .7s linear infinite; }
@keyframes chat-invite-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
@keyframes chat-invite-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .chat-invite { animation: none; } }
`]
})
export class ChatInvitesComponent {
  @Input() invites: ChatInvite[] = [];
  /** O convite com uma resposta a caminho (botoes desligados). */
  @Input() busy: string | null = null;
  /** Base dos nomes de ficheiro das fotos (`/users/files/`). */
  @Input() profileBase = '';
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() accept = new EventEmitter<string>();
  @Output() decline = new EventEmitter<string>();

  text(i: ChatInvite): string {
    const by = (i.by && i.by.name) || '';
    const client = i.client && i.client.name;
    return client
      ? this.strings.inviteFrom.replace('{by}', by).replace('{client}', client).trim()
      : this.strings.inviteFromNoClient.replace('{by}', by).trim();
  }

  ago(value: any): string {
    return formatAgo(value, this.strings);
  }

  photo(profile?: string): string | undefined {
    if (!profile) {
      return undefined;
    }
    return /^(https?:)?\//.test(profile) || profile.indexOf('data:') === 0 ? profile : this.profileBase + profile;
  }

  trackByChat(_: number, i: ChatInvite): string {
    return i.chat;
  }
}
